import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebaseConfig";
import { collection, query, where, orderBy, limit, getDocs, startAfter, doc, deleteDoc, } from "firebase/firestore";
import * as XLSX from "xlsx-js-style";
import Button from "../components/Button";
import { useClaims } from "../providers/ClaimsProvider";

const formatDateDMY = (f) => {
  if (!f) return "—";
  if (typeof f === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f)) {
    const [y, m, d] = f.split("-");
    return `${d}-${m}-${y}`;
  }
  if (f?.seconds) {
    const d = new Date(f.seconds * 1000);
    if (!isNaN(d)) return d.toLocaleDateString("es-ES");
  }
  const d = new Date(f);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("es-ES");
};

const col = (prefix, pregunta) =>
  `${prefix}_${pregunta}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

// ---------------------
// EAT-10
// ---------------------
const EAT10_PREGUNTAS = {
  "0": "1. Mi problema para tragar me ha llevado a perder peso",
  "1": "2. Mi problema para tragar interfiere con mi capacidad para comer fuera de casa",
  "2": "3. Tragar líquidos me supone un esfuerzo extra",
  "3": "4. Tragar sólidos me supone un esfuerzo extra",
  "4": "5. Tragar pastillas me supone un esfuerzo extra",
  "5": "6. Tragar es doloroso",
  "6": "7. El placer de comer se ve afectado por mi problema para tragar",
  "7": "8. Cuando trago, la comida se pega en mi garganta",
  "8": "9. Toso cuando como",
  "9": "10. Tragar es estresante",
};

const EAT10_RESPUESTAS = {
  "0": "0 - Ningún problema",
  "4": "4 - Problema serio",
};

// ---------------------
// CRIBADO NUTRICIONAL
// ---------------------

const MNA_PREGUNTAS = {
  A: "A. ¿Ha perdido el apetito?... últimos 3 meses?",
  B: "B. Pérdida reciente de peso (< 3 meses)",
  C: "C. Movilidad",
  D: "D. ¿Enfermedad aguda o estrés psicológico en los últimos 3 meses?",
  E: "E. Problemas neuropsicológicos",
  F: "F. Índice de masa corporal (IMC)",
  G: "G. ¿El paciente vive independiente en su domicilio?",
  H: "H. ¿Toma más de 3 medicamentos al día?",
  I: "I. ¿Úlceras o lesiones cutáneas?",
  J: "J. ¿Cuántas comidas completas toma al día?",
  K: "K. ¿Consume el paciente…",
  K1: "…productos lácteos al menos una vez al día?",
  K2: "…huevos o legumbres 1 o 2 veces a la semana?",
  K3: "…carne, pescado o aves, diariamente?",
  L: "L. ¿Consume frutas o verduras al menos 2 veces al día?",
  M: "M. ¿Cuántos vasos de agua u otros líquidos toma al día?",
  N: "N. Forma de alimentarse",
  O: "O. ¿Se considera el paciente que está bien nutrido?",
  P: "P. En comparación con las personas de su edad, ¿cómo encuentra su estado de salud?",
  Q: "Q. Circunferencia braquial (CB en cm)",
  R: "R. Circunferencia de la pantorrilla (CP en cm)",
};

const MNA_RESPUESTAS = {
  A: {
    "0": "0 - ha comido mucho menos",
    "1": "1 - ha comido menos",
    "2": "2 - ha comido igual",
  },
  B: {
    "0": "0 - > 3 kg",
    "1": "1 - no lo sabe",
    "2": "2 - pérdida entre 1 a 3 kg",
    "3": "3 - no ha habido peso",
  },
  C: {
    "0": "0 - de la cama al sillón",
    "1": "1 - autonomía en el interior",
    "2": "2 - sale del domicilio",
  },
  D: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  E: {
    "0": "0 - demencia o depresión grave",
    "1": "1 - demencia leve",
    "2": "2 - sin problemas psicológicos",
  },
  F: {
    "0": "0 - IMC < 19",
    "1": "1 - 19 ≤ IMC < 21",
    "2": "2 - 21 ≤ IMC < 23",
    "3": "3 - IMC ≥ 23",
  },
  G: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  H: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  I: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  J: {
    "0": "0 - 1 comida",
    "1": "1 - 2 comidas",
    "2": "2 - 3 comidas",
  },
  K1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  L: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  M: {
    "0": "0 - menos de 3 vasos",
    "0.5": "0.5 - de 3 a 5 vasos",
    "1": "1 - más de 5 vasos",
  },
  N: {
    "0": "0 - necesita ayuda",
    "1": "1 - se alimenta solo con dificultad",
    "2": "2 - se alimenta solo sin dificultad",
  },
  O: {
    "0": "0 - malnutrición grave",
    "1": "1 - no lo sabe o malnutrición moderada",
    "2": "2 - sin problemas de nutrición",
  },
  P: {
    "0": "0 - peor",
    "0.5": "0.5 - no lo sabe",
    "1": "1 - igual",
    "2": "2 - mejor",
  },
  Q: {
    "0": "0 - CB < 21",
    "0.5": "0.5 - 21 ≤ CB ≤ 22",
    "1": "1 - CB > 22",
  },
  R: {
    "0": "0 - CP < 31",
    "1": "1 - CP ≥ 31",
  },
};

const MST_PREGUNTAS = {
  q1: "1. ¿Ha perdido peso de manera involuntaria recientemente?",
  q1b: "Si ha perdido peso, ¿cuántos kilogramos?",
  q2: "2. ¿Ha comido menos de lo normal a causa de la disminución del apetito?",
};

const MST_RESPUESTAS = {
  q1: {
    "0": "0 - no",
    "2": "2 - no estoy seguro",
  },
  q1b: {
    "1": "1 - de 1 a 5 kg",
    "2": "2 - de 6 a 10 kg",
    "3": "3 - de 11 a 15 kg",
    "4": "4 - > 15 kg",
    "0": "0 - no estoy seguro",
  },
  q2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const NRS_PREGUNTAS = {
  n1: "1. ¿IMC < 20.5?",
  n2: "2. ¿Pérdida de peso en los últimos 3 meses?",
  n3: "3. ¿Disminución de la ingesta en la última semana?",
  n4: "4. ¿Enfermedad grave?",
};

const NRS_RESPUESTAS = {
  n1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n4: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const MUST_PREGUNTAS = {
  step1: "Paso 1. IMC = peso en kg / talla en m²",
  step2: "Paso 2. Pérdida de peso involuntaria en los últimos 3-6 meses",
  step3: "Paso 3. Paciente con enfermedad aguda y sin aporte > 5 días",
};

const MUST_RESPUESTAS = {
  step1: {
    "0": "0 - ≥ 20",
    "1": "1 - 18.5 a 20",
    "2": "2 - ≤ 18.5",
  },
  step2: {
    "0": "0 - ≤ 5 %",
    "1": "1 - 5 a 10 %",
    "2": "2 - ≥ 10 %",
  },
  step3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

// ---------------------
// GLIM – Preguntas
// ---------------------
const GLIM_PREGUNTAS = {
  p1: "CRITERIOS FENOTÍPICOS - 1. Pérdida de peso",
  p2: "CRITERIOS FENOTÍPICOS - 2. Índice de Masa Corporal (IMC)",
  p3: "CRITERIOS FENOTÍPICOS - 3. Masa muscular reducida",
  p4: "CRITERIOS ETIOLÓGICOS - 4. Ingesta o asimilación reducida",
  p5: "CRITERIOS ETIOLÓGICOS - 5. Enfermedad o inflamación",
};

// ---------------------
// CAVIDAD-ORAL – Preguntas
// ---------------------
const CAVIDAD_ORAL_PREGUNTAS = {
  q1: "1. Piezas dentarias",
  q2: "2. Dentadura postiza",
  q3: "3. Mucositis",
  q4: "4. Lengua y encías",
  q5: "5. Salivación",
  q6: "6. Halitosis (mal aliento)",
  q7: "7. Dolor o molestias orales",
};

// ---------- Clasificación cribado nutricional ----------
const clasificarCribadoNutricional = (tipoCribado, puntuacion) => {
  switch (tipoCribado) {
    case "MNA":
      if (puntuacion > 24) return "Estado nutricional normal > 24";
      if (puntuacion >= 17) return "Riesgo de malnutrición 17-24";
      return "Malnutrición < 17";

    case "MST":
      return puntuacion >= 2
        ? "Riesgo de malnutrición"
        : "Sin riesgo";

    case "NRS":
      return puntuacion >= 3
        ? "Riesgo de malnutrición ≥ 3"
        : "Sin riesgo";

    case "MUST":
      if (puntuacion === 0) return "Riesgo bajo = 0";
      if (puntuacion === 1) return "Riesgo medio = 1";
      return "Riesgo alto = 2 o +";

    default:
      return "";
  }
};

export default function PacientesLista() {
  const navigate = useNavigate();
  const { user, role, loading, roleDb } = useClaims();
  const [cargando, setCargando] = useState(true);
  const [pacientesCloud, setPacientesCloud] = useState([]);
  const [qText, setQText] = useState("");

  // Selección
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const isSelected = (id) => selectedIds.has(id);
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const selectVisible = (lista) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      lista.forEach((p) => next.add(p.id));
      return next;
    });

  // Rol efectivo: primero Firestore, luego claims, luego "personal"
  const effectiveRole = roleDb ?? role ?? "personal";
  const esElevado =
    effectiveRole === "investigador_principal" ||
    effectiveRole === "jefe" ||
    effectiveRole === "investigador";

  const formatFechaES = (valor) => {
    if (!valor) return "—";
    let d;
    if (typeof valor?.toDate === "function") d = valor.toDate();
    else if (valor instanceof Date) d = valor;
    else {
      const tmp = new Date(valor);
      if (!isNaN(tmp)) d = tmp;
    }
    if (!d) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Cargar pacientes según rol
  useEffect(() => {
    const load = async () => {
      if (loading) return;
      setCargando(true);
      try {
        if (!user) {
          setPacientesCloud([]);
          return;
        }

        let qq;
        if (esElevado) {
          qq = query(
            collection(db, "pacientes"),
            orderBy("fecha", "desc"),
            orderBy("__name__"),
            limit(10)
          );
        } else {
          qq = query(
            collection(db, "pacientes"),
            where("creadoPor", "==", user.uid),
            orderBy("fecha", "desc"),
            orderBy("__name__"),
            limit(10)
          );
        }

        const snap = await getDocs(qq);
        setPacientesCloud(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setCargando(false);
      }
    };

    load();
  }, [user, esElevado, loading]);

  const filtrados = useMemo(() => {
    const term = qText.trim().toLowerCase();
    if (!term) return pacientesCloud;
    return pacientesCloud.filter(
      (p) =>
        String(p.codigo || "").toLowerCase().includes(term) ||
        String(p.sexo || "").toLowerCase().includes(term)
    );
  }, [qText, pacientesCloud]);

const mapToExcelRow = (p) => ({
  "Código": p.codigo || "",
  "Fecha de registro": formatFechaES(p.fecha),
  "Sexo": p.sexo || "",
  "Edad": p.edad ?? "",
  "Peso (kg)": p.peso ?? "",
  "Talla (cm)": p.talla ?? "",
  "IMC": p.imc ?? "",
  "Creado por...": p.creadoPorEmail || p.creadoPor || "—",
});

const aplicarFormatoBasicoExcel = (ws) => {
  ws["!cols"] = [
    { wch: 25 }, // Código
    { wch: 18 }, // Fecha de registro
    { wch: 12 }, // Sexo
    { wch: 10 }, // Edad
    { wch: 16 }, // Peso
    { wch: 16 }, // Talla
    { wch: 14 }, // IMC
    { wch: 45 }, // Creado por
  ];

  if (!ws["!ref"]) return;

  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        font: {
          bold: R === 0,
          sz: 12,
        },
      };
    }
  }
};

  // Exportar TODO a Excel (según rol)
  const exportarTodoExcel = async () => {
    if (!user) return;

    const pageSize = 500;
    let acc = [];
    let last;

    while (true) {
      let qq;
      if (esElevado) {
        qq = last
          ? query(
              collection(db, "pacientes"),
              orderBy("fecha", "desc"),
              orderBy("__name__"),
              startAfter(last.get("fecha"), last.id),
              limit(pageSize)
            )
          : query(
              collection(db, "pacientes"),
              orderBy("fecha", "desc"),
              orderBy("__name__"),
              limit(pageSize)
            );
      } else {
        qq = last
          ? query(
              collection(db, "pacientes"),
              where("creadoPor", "==", user.uid),
              orderBy("fecha", "desc"),
              orderBy("__name__"),
              startAfter(last.get("fecha"), last.id),
              limit(pageSize)
            )
          : query(
              collection(db, "pacientes"),
              where("creadoPor", "==", user.uid),
              orderBy("fecha", "desc"),
              orderBy("__name__"),
              limit(pageSize)
            );
      }

      const snap = await getDocs(qq);
      if (snap.empty) break;

      acc = acc.concat(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      last = snap.docs[snap.docs.length - 1];
      if (snap.size < pageSize) break;
    }

    const rows = acc.map(mapToExcelRow);
    const wb = XLSX.utils.book_new();
const ws = XLSX.utils.json_to_sheet(rows);
aplicarFormatoBasicoExcel(ws);
XLSX.utils.book_append_sheet(wb, ws, "Pacientes");
    XLSX.writeFile(wb, `pacientes_todos.xlsx`);
  };

  const exportarSeleccionados = () => {
    if (selectedIds.size === 0) {
      alert("No hay registros seleccionados.");
      return;
    }
    const seleccionados = pacientesCloud.filter((p) => selectedIds.has(p.id));
    const rows = seleccionados.map(mapToExcelRow);
    const wb = XLSX.utils.book_new();
const ws = XLSX.utils.json_to_sheet(rows);
aplicarFormatoBasicoExcel(ws);
XLSX.utils.book_append_sheet(wb, ws, "Seleccionados");
    XLSX.writeFile(wb, `pacientes_seleccionados.xlsx`);
  };

const exportarPacienteDatasetPlano = async (p) => {
  try {
    const dataset = [];

    // ======================
    // Cargar visitas
    // ======================
    const visitasSnap = await getDocs(
      query(
        collection(db, "pacientes", p.id, "visitas"),
        orderBy("fecha", "asc")
      )
    );

    for (let i = 0; i < visitasSnap.docs.length; i++) {
      const vDoc = visitasSnap.docs[i];
      const v = vDoc.data();
      const numero = i + 1;

      // ======================
      // Fila base (una visita)
      // ======================
      const row = {
        codigo_paciente: p.codigo || "",
        numero_visita: numero,
        fecha_visita: formatDateDMY(v.fecha),
        sexo: p.sexo || "",
        edad: p.edad ?? "",
        peso: v.medidas?.peso ?? v.peso ?? "",
        talla: v.medidas?.talla ?? v.talla ?? "",
        imc: v.medidas?.imc ?? v.imc ?? "",
      };

      // ======================
      // Respuestas
      // ======================
      const respSnap = await getDocs(
        collection(
          db,
          "pacientes",
          p.id,
          "visitas",
          vDoc.id,
          "respuestas"
        )
      );

      respSnap.forEach((r) => {
        const q = r.data();
        const instrumentoId = q.instrumentoId || r.id;

        // ======================
        // EAT-10
        // ======================
        if (instrumentoId === "EAT-10") {
          let total = 0;

          Object.entries(q.items || {}).forEach(([k, v]) => {
            const pregunta = EAT10_PREGUNTAS[k] || k;
            const n = Number(v || 0);
            total += n;

            row[col("eat10", pregunta)] =
              EAT10_RESPUESTAS[String(v)] ?? String(v ?? "");
          });

          row.eat10_puntuacion_total = total;
          row.eat10_resultado =
            total >= 3
              ? "Presenta riesgo de disfagia"
              : "No presenta riesgo de disfagia";
        }

        // ======================
        // CRIBADO NUTRICIONAL
        // ======================
        if (instrumentoId === "CRIBADO-NUTRICIONAL") {
          const tipo = q.items?.__tipoPaciente;

          const respuestas =
            tipo === "MNA"  ? q.items?.mna  :
            tipo === "MST"  ? q.items?.mst  :
            tipo === "NRS"  ? q.items?.nrs  :
            tipo === "MUST" ? q.items?.must :
            {};

          let total = 0;

          Object.entries(respuestas || {}).forEach(([k, v]) => {
            if (tipo === "MNA" && ["K1", "K2", "K3"].includes(k)) return;

            const pregunta =
              tipo === "MNA"  ? MNA_PREGUNTAS[k]  :
              tipo === "MST"  ? MST_PREGUNTAS[k]  :
              tipo === "NRS"  ? NRS_PREGUNTAS[k]  :
              tipo === "MUST" ? MUST_PREGUNTAS[k] :
              k;

            const respuesta =
              tipo === "MNA"  ? MNA_RESPUESTAS[k]?.[String(v)] :
              tipo === "MST"  ? MST_RESPUESTAS[k]?.[String(v)] :
              tipo === "NRS"  ? NRS_RESPUESTAS[k]?.[String(v)] :
              tipo === "MUST" ? MUST_RESPUESTAS[k]?.[String(v)] :
              String(v ?? "");

            const n = Number(v);
            if (!isNaN(n)) total += n;

            row[col(`cribado_${tipo.toLowerCase()}`, pregunta)] =
              respuesta ?? "";
          });

          row.cribado_tipo = tipo;
          row.cribado_puntuacion_total = total;
          row.cribado_clasificacion =
            clasificarCribadoNutricional(tipo, total);
        }

        // ======================
        // GLIM
        // ======================
        if (instrumentoId === "CRITERIOS-GLIM") {
          Object.entries(q.items?.glim || {}).forEach(([k, v]) => {
            const pregunta = GLIM_PREGUNTAS[k] || k;
            row[col("glim", pregunta)] = String(v ?? "");
          });

          row.glim_resultado = q.resultado || "";
        }

        // ======================
        // CAVIDAD ORAL
        // ======================
        if (instrumentoId === "CAVIDAD-ORAL") {
          Object.entries(q.items?.cavidadoral || {}).forEach(([k, v]) => {
            const pregunta = CAVIDAD_ORAL_PREGUNTAS[k] || k;
            row[col("cavidad_oral", pregunta)] = String(v ?? "");
          });
        }

        // ======================
        // MECV-V
        // ======================
        if (instrumentoId === "MECV-V") {
          Object.entries(q.items?.mecvv || {}).forEach(([k, v]) => {
            if (!v) return;
            row[col("mecvv", k.replace(/_/g, " "))] = "Sí";
          });

          row.mecvv_resultado = q.resultado || "";
        }
      });

      dataset.push(row);
    }

    // ======================
    // Normalizar columnas
    // ======================
    const allKeys = Array.from(
      new Set(dataset.flatMap((r) => Object.keys(r)))
    );

    const datasetNormalizado = dataset.map((r) => {
      const o = {};
      allKeys.forEach((k) => {
        o[k] = r[k] ?? "";
      });
      return o;
    });

    // ======================
    // Excel
    // ======================
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(datasetNormalizado);

    ws["!cols"] = allKeys.map(() => ({ wch: 35 }));

    XLSX.utils.book_append_sheet(wb, ws, "DATASET_PLANO");

    XLSX.writeFile(
      wb,
      `paciente_${p.codigo || p.id}_dataset_plano.xlsx`
    );
  } catch (e) {
    console.error(e);
    alert("No se pudo generar el dataset plano.");
  }
};

  const eliminarPaciente = async (p) => {
    const etiqueta = p.codigo || p.id;
    const ok = window.confirm(
      `¿Eliminar el paciente "${etiqueta}"? Esta acción no se puede deshacer.`
    );
    if (!ok) return;

    if (!esElevado && p.creadoPor !== user.uid) {
      alert("No tienes permiso para eliminar este paciente.");
      return;
    }

    try {
      await deleteDoc(doc(db, "pacientes", p.id));
      setPacientesCloud((prev) => prev.filter((x) => x.id !== p.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(p.id);
        return next;
      });
      alert("Paciente eliminado.");
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar (permiso o error de red).");
    }
  };

  if (loading)
    return (
      <div className="p-4 text-center">
        <p>Cargando datos...</p>
      </div>
    );

  if (!user) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <h3 className="font-bold text-center text-lg">Pacientes</h3>
        <p className="mt-4 text-justify">
          Debes iniciar sesión. Solo el creador de cada registro y quienes
          tengan rol de <strong>investigador principal</strong> pueden
          visualizar esta información.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-xl mx-auto flex flex-col gap-4">
      {/* Encabezado */}
      <div className="flex items-center justify-center">
        <h3 className="font-bold text-center">MI BASE DE DATOS</h3>
      </div>

      {/* Párrafos */}
      <div className="flex flex-col gap-3 text-justify">
        <p>
          Puedes acceder al informe pinchando en Ver/Editar (permitiendo
          visualizar o modificar la información del paciente). Si queremos
          descargar un informe formato .xlsx, ya sea individual y/o grupal, o
          borrar algún registro, debe pulsar sobre los botones
          correspondientes. También puedes{" "}
          <strong>seleccionar registros</strong> y exportarlos en bloque.
        </p>
      </div>

      {/* Acciones Excel */}
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <Button
          onClick={exportarTodoExcel}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {esElevado
            ? "Exportar Excel (todos los pacientes)"
            : "Exportar Excel (mis pacientes)"}
        </Button>

        <Button
          onClick={exportarSeleccionados}
          className={`text-white ${
            selectedIds.size === 0
              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
              : "bg-yellow-500 hover:bg-yellow-600"
          }`}
          disabled={selectedIds.size === 0}
        >
          {esElevado
            ? `Exportar seleccionados (${selectedIds.size})`
            : `Exportar seleccionados (míos) (${selectedIds.size})`}
        </Button>
      </div>

      {/* Buscador */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Buscar por código o sexo…"
          value={qText}
          onChange={(e) => setQText(e.target.value)}
          className="border border-gray-300 rounded-lg p-2 w-full"
        />
      </div>

      {/* Barra selección */}
      <div className="flex items-center gap-2 text-sm">
        <Button
          onClick={() => selectVisible(filtrados)}
          className="bg-slate-600 hover:bg-slate-700 text-white"
        >
          Seleccionar visibles ({filtrados.length})
        </Button>
        <Button
          onClick={clearSelection}
          className="bg-gray-500 hover:bg-gray-600 text-white"
        >
          Limpiar selección
        </Button>
      </div>

      {/* Lista */}
      <div className="mt-2">
        {cargando ? (
          <p>Cargando pacientes…</p>
        ) : filtrados.length === 0 ? (
          <p>No hay pacientes para mostrar.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filtrados.map((p) => {
              const etiqueta = p.codigo || p.id;
              const selected = isSelected(p.id);
              return (
                <li
                  key={p.id}
                  onClick={() => toggleSelect(p.id)}
                  className={`py-3 px-2 rounded-lg transition cursor-pointer
                    ${
                      selected
                        ? "bg-blue-50 ring-2 ring-yellow-400"
                        : "hover:bg-gray-50"
                    }
                    flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}
                >
                  {/* Izquierda */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelect(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-5 w-5 accent-blue-600"
                    />
                    <div>
                      <div className="font-semibold">{etiqueta}</div>
                      <div className="text-gray-700">
                        Fecha: {formatFechaES(p.fecha)}
                      </div>
                    </div>
                  </div>

                  {/* Derecha */}
                  <div className="flex gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pacientes/${encodeURIComponent(p.id)}`);
                      }}
                    >
                      Ver/Editar
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        exportarPacienteDatasetPlano(p);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Excel
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        eliminarPaciente(p);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Eliminar
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}